import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ConfirmView, RecordStatus, SignResponse } from '@sign/shared';
import { fetchConfirm, finalizeSheet, HttpError, sendSmsCode, submitSignature } from './api/client';
import { parseTokenFromLocation } from './utils/token';
import ContentView from './components/ContentView';
import SignaturePad from './components/SignaturePad';
import StatusBadge from './components/StatusBadge';
import SuccessView from './components/SuccessView';
import QrPanel from './components/QrPanel';
import Footer from './components/Footer';
import DocSignView from './components/DocSignView';
import { composeSheetImage } from './utils/composeSheet';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; view: ConfirmView };

export default function App() {
  const token = useMemo(() => parseTokenFromLocation(), []);
  const currentUrl = useMemo(() => window.location.href, []);

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  // Multi-sign: this visitor already added their signature (record still partial).
  const [justSigned, setJustSigned] = useState(false);
  // SMS identity verification inputs (when the sheet requires it).
  const [vPhone, setVPhone] = useState('');
  const [vCode, setVCode] = useState('');
  const [smsLeft, setSmsLeft] = useState(0);
  const [smsError, setSmsError] = useState('');

  useEffect(() => {
    if (smsLeft <= 0) return;
    const t = window.setTimeout(() => setSmsLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [smsLeft]);

  const handleSendSms = useCallback(() => {
    if (!token || smsLeft > 0) return;
    setSmsError('');
    sendSmsCode(token, vPhone.trim())
      .then(() => setSmsLeft(60))
      .catch((err: unknown) => {
        setSmsError(err instanceof HttpError ? err.message : '验证码发送失败，请重试。');
      });
  }, [token, vPhone, smsLeft]);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ phase: 'error', message: '链接无效：缺少签字凭证。' });
      return;
    }
    fetchConfirm(token)
      .then((view) => {
        if (!cancelled) setState({ phase: 'ready', view });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof HttpError
            ? err.message
            : '加载失败，请检查网络后重试。';
        setState({ phase: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = useCallback(
    (dataUrl: string) => {
      if (state.phase !== 'ready') return;
      const { view } = state;
      const isMulti = view.signerCount > 1;
      setSubmitting(true);
      setSubmitError('');
      // Single-sign with composeSheet: store the whole composed sheet as the
      // one archival image (fall back to the bare signature on failure).
      // Multi-sign / composeSheet=false: store the bare signature; multi
      // composes the final sheet only after the last signature (finalize).
      const now = Date.now();
      const prepare: Promise<string> =
        !isMulti && view.composeSheet
          ? composeSheetImage({
              sheetName: view.sheetName,
              content: view.content,
              signatures: [{ src: dataUrl, signedAt: now }],
              signedAt: now,
            }).catch(() => dataUrl)
          : Promise.resolve(dataUrl);
      const slotName = isMulti ? view.signerNames?.[view.signedCount]?.trim() || '' : '';
      prepare
        .then((imageBase64) =>
          submitSignature(token, {
            imageBase64,
            ...(slotName ? { signerIdentity: slotName } : {}),
            ...(view.verifyRequired ? { phone: vPhone.trim(), smsCode: vCode.trim() } : {}),
          }),
        )
        .then(async (res: SignResponse) => {
          // Last signer of a multi-sign sheet composes the archival image
          // from every collected signature. Best-effort — the bare
          // signatures remain the fallback if this fails.
          if (isMulti && res.status === 'done' && view.composeSheet) {
            try {
              const nameOf = (i: number) => view.signerNames?.[i]?.trim() || '';
              const sigs = [
                ...(view.signatures ?? []).map((s, i) => ({
                  src: s.url,
                  signedAt: s.signedAt,
                  label: nameOf(i),
                })),
                { src: dataUrl, signedAt: res.signedAt, label: slotName },
              ];
              const composed = await composeSheetImage({
                sheetName: view.sheetName,
                content: view.content,
                signatures: sigs,
                signedAt: res.signedAt,
              });
              await finalizeSheet(token, composed);
            } catch {
              /* keep bare signatures */
            }
          }
          setState({
            phase: 'ready',
            view: {
              ...state.view,
              status: res.status === 'done' ? 'done' : 'partial',
              signedCount: res.signedCount,
              signatures: [
                ...(state.view.signatures ?? []),
                ...(res.signatureUrl ? [{ url: res.signatureUrl, signedAt: res.signedAt }] : []),
              ],
              signedAt: res.status === 'done' ? res.signedAt : state.view.signedAt,
              signatureUrl: res.status === 'done' ? res.signatureUrl : state.view.signatureUrl,
            },
          });
          if (res.status === 'partial') setJustSigned(true);
        })
        .catch((err: unknown) => {
          if (err instanceof HttpError) {
            setSubmitError(err.message);
            // 409 already_signed / 410 revoked → reflect a terminal state.
            if (err.status === 409) {
              setState((s) =>
                s.phase === 'ready'
                  ? { phase: 'ready', view: { ...s.view, status: 'done' } }
                  : s,
              );
            } else if (err.status === 410) {
              setState((s) =>
                s.phase === 'ready'
                  ? { phase: 'ready', view: { ...s.view, status: 'revoked' } }
                  : s,
              );
            }
          } else {
            setSubmitError('提交失败，请重试。');
          }
        })
        .finally(() => setSubmitting(false));
    },
    [state, token, vPhone, vCode],
  );

  // 文档叠加签署提交：DocSignView 已烤好成品图，直接作为归档图提交（单签路径）
  const handleDocSubmit = useCallback(
    (composedDataUrl: string) => {
      if (state.phase !== 'ready') return;
      setSubmitting(true);
      setSubmitError('');
      submitSignature(token, { imageBase64: composedDataUrl })
        .then((res: SignResponse) => {
          setState({
            phase: 'ready',
            view: {
              ...state.view,
              status: 'done',
              signedAt: res.signedAt,
              signatureUrl: res.signatureUrl,
            },
          });
        })
        .catch((err: unknown) => {
          if (err instanceof HttpError) {
            setSubmitError(err.message);
            if (err.status === 409) {
              setState((s) => (s.phase === 'ready' ? { phase: 'ready', view: { ...s.view, status: 'done' } } : s));
            } else if (err.status === 410) {
              setState((s) => (s.phase === 'ready' ? { phase: 'ready', view: { ...s.view, status: 'revoked' } } : s));
            }
          } else {
            setSubmitError('提交失败，请重试。');
          }
        })
        .finally(() => setSubmitting(false));
    },
    [state, token],
  );

  if (state.phase === 'loading') {
    return (
      <div className="screen screen--center">
        <div className="spinner" aria-label="加载中" />
        <p className="muted">加载中…</p>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className="screen screen--center">
        <div className="error-card">
          <p className="error-card__title">无法打开确认单</p>
          <p className="muted">{state.message}</p>
        </div>
      </div>
    );
  }

  const { view } = state;
  const isDone = view.status === 'done';
  const isTerminal: boolean =
    view.status === 'done' || view.status === 'expired' || view.status === 'revoked';

  // 文档叠加签署（sign v2）：有签名区域 + 文档底图时走整页文档签署
  const docSrc = view.content?.[0]?.images?.[0];
  const isDocOverlay = !!(view.signAreas && view.signAreas.length > 0 && docSrc);


  return (
    <div className="page">
      <div className="page__inner">
        <main className="card">
          <header className="header">
            <h1 className="header__title">{view.sheetName}</h1>
            <StatusBadge status={view.status} />
          </header>

          {/* 文档叠加签署：底图即内容，不再单列确认内容 */}
          {view.type === 'content_sign' && !isDocOverlay && (
            <section className="section">
              <h2 className="section__label">确认内容</h2>
              <ContentView content={view.content} />
            </section>
          )}

          <section className="section">
            {isDone ? (
              <SuccessView signedAt={view.signedAt} signatureUrl={view.signatureUrl} />
            ) : isTerminal ? (
              <TerminalNotice status={view.status} />
            ) : isDocOverlay ? (
              <>
                {submitError && <p className="form-error">{submitError}</p>}
                <DocSignView
                  docSrc={docSrc as string}
                  areas={view.signAreas as NonNullable<typeof view.signAreas>}
                  submitting={submitting}
                  onComplete={handleDocSubmit}
                />
              </>
            ) : (
              <>
                <h2 className="section__label">
                  签字
                  {view.signerCount > 1 && (
                    <span className="muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
                      已签 {view.signedCount}/{view.signerCount} 人
                    </span>
                  )}
                </h2>
                {view.signerCount > 1 && (
                  <div style={{ marginBottom: 12 }}>
                    {Array.from({ length: view.signerCount }, (_, i) => {
                      const name = view.signerNames?.[i]?.trim() || `第 ${i + 1} 位签字人`;
                      const sig = view.signatures?.[i];
                      const isCurrent = !sig && i === view.signedCount && !justSigned;
                      return (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '7px 10px',
                            border: `1px solid ${isCurrent ? '#1a1a1a' : '#ececec'}`,
                            borderRadius: 8,
                            marginBottom: 6,
                            background: sig ? '#fafafa' : '#fff',
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0 }}>
                            {name}
                          </span>
                          {sig ? (
                            <img
                              src={sig.url}
                              alt={`${name}的签名`}
                              style={{ height: 40, maxWidth: 120, objectFit: 'contain' }}
                            />
                          ) : (
                            <span className="muted" style={{ fontSize: 12 }}>
                              {isCurrent ? '轮到 TA 签字 ↓' : '待签'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {view.verifyRequired && !justSigned && (
                  <div
                    style={{
                      border: '1px solid #ececec',
                      borderRadius: 10,
                      padding: '12px 14px',
                      marginBottom: 14,
                      background: '#fafafa',
                    }}
                  >
                    <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
                      身份验证
                    </div>
                    <p className="muted" style={{ fontSize: 12, margin: '0 0 10px' }}>
                      {view.verifyPhoneMask
                        ? `请输入预留手机号（${view.verifyPhoneMask}）并获取短信验证码`
                        : '请输入手机号并获取短信验证码'}
                    </p>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={11}
                        placeholder="手机号"
                        value={vPhone}
                        onChange={(e) => setVPhone(e.target.value.replace(/\D/g, ''))}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          height: 40,
                          padding: '0 12px',
                          borderRadius: 8,
                          border: '1px solid #ddd',
                          fontSize: 15,
                        }}
                      />
                      <button
                        type="button"
                        className="btn"
                        onClick={handleSendSms}
                        disabled={smsLeft > 0 || !/^1\d{10}$/.test(vPhone)}
                        style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                      >
                        {smsLeft > 0 ? `${smsLeft}s 后重发` : '获取验证码'}
                      </button>
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="短信验证码"
                      value={vCode}
                      onChange={(e) => setVCode(e.target.value.replace(/\D/g, ''))}
                      style={{
                        width: '100%',
                        height: 40,
                        padding: '0 12px',
                        borderRadius: 8,
                        border: '1px solid #ddd',
                        fontSize: 15,
                        boxSizing: 'border-box',
                      }}
                    />
                    {smsError && (
                      <p className="form-error" style={{ marginTop: 8 }}>
                        {smsError}
                      </p>
                    )}
                  </div>
                )}
                {submitError && <p className="form-error">{submitError}</p>}
                {justSigned ? (
                  <div className="terminal-notice">
                    <p className="muted">
                      你已签字。还差 {view.signerCount - view.signedCount}{' '}
                      人签字后本单生效，可把当前链接转发给下一位签字人。
                    </p>
                  </div>
                ) : (
                  (() => {
                    const locked =
                      view.verifyRequired && (!/^1\d{10}$/.test(vPhone) || vCode.length !== 6);
                    return (
                      <div style={{ position: 'relative' }}>
                        <SignaturePad
                          onSubmit={handleSubmit}
                          submitting={submitting}
                          disabled={locked}
                        />
                        {locked && (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'rgba(255,255,255,0.72)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 12,
                              pointerEvents: 'none',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 14,
                                color: '#555',
                                background: '#fff',
                                border: '1px solid #e5e5e5',
                                borderRadius: 8,
                                padding: '8px 14px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                              }}
                            >
                              🔒 请先完成上方手机号验证后签字
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </>
            )}
          </section>

          <Footer />
        </main>

        <QrPanel url={currentUrl} />
      </div>
    </div>
  );
}

function TerminalNotice({ status }: { status: RecordStatus }) {
  const text = status === 'expired' ? '该确认单已过期，无法签字。' : '该确认单已撤销，无法签字。';
  return (
    <div className="terminal-notice">
      <p className="muted">{text}</p>
    </div>
  );
}
